// Input de códigos postales con chips. Texto libre (no hay catálogo de CP: son datos
// declarados), con validación de formato en el momento de escribirlos.
//
// Por qué no `SearchDropdown`: es un selector cerrado sobre una lista conocida. Acá no
// existe esa lista —los CP argentinos no tienen catálogo abierto y el operador los
// escribe o los pega— y hacen falta VARIOS valores por regla.
//
// Formatos aceptados (los mismos que el backend, `postal-code.util.ts`):
//   - CPA argentino: letra + 4 dígitos + 3 letras   ("M5500ABC")
//   - CP de 4 dígitos                                ("5500")
//   - letra + 4 dígitos (CPA truncado)               ("M5500" → se guarda como "5500")
// Todo lo demás queda marcado como inválido: se muestra pero NO se envía (`validPostalCodes`).
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, AlertCircle } from "lucide-react";

const CPA_RE = /^[A-Z]\d{4}[A-Z]{3}$/;
const CP4_RE = /^\d{4}$/;
const LETTER_CP4_RE = /^[A-Z]\d{4}$/;
const SEPARATORS_RE = /[\s-]+/g;

/**
 * Espejo EXACTO de `normalizePostalCode` del backend (microservice-billing y
 * microservice-packages). Si divergen, la UI acepta reglas que el motor nunca matchea.
 * @returns {string|null} clave canónica de la regla (CPA de 8 chars o CP4), o null.
 */
export const postalRuleKey = (raw) => {
  if (raw === null || raw === undefined || typeof raw === "object") return null;
  const value = String(raw).trim().toUpperCase().replace(SEPARATORS_RE, "");
  if (!value) return null;
  if (CPA_RE.test(value)) return value;
  if (CP4_RE.test(value)) return value;
  if (LETTER_CP4_RE.test(value)) return value.slice(1, 5);
  return null;
};

/** ¿El texto que escribió el usuario es un CP tarifable? */
export const isValidPostalCode = (raw) => postalRuleKey(raw) !== null;

/** Códigos tarifables de una lista, ya en su clave canónica y sin duplicados. */
export const validPostalCodes = (codes) => {
  const out = [];
  (Array.isArray(codes) ? codes : []).forEach((c) => {
    const key = postalRuleKey(c);
    if (key && !out.includes(key)) out.push(key);
  });
  return out;
};

// Un pegado puede traer separadores de lista: coma, punto y coma, salto de línea o tab.
// El espacio NO separa: "M 5500 ABC" es un solo CPA escrito con espacios.
const LIST_SEPARATORS_RE = /[,;\n\t]+/;

/**
 * @param {string[]} value        códigos actuales (tal como los escribió el operador)
 * @param {Function} onChange     recibe el arreglo nuevo
 * @param {string}   label        rótulo visible (y `aria-label` del input)
 * @param {boolean}  disabled
 */
export default function PostalCodeChipsInput({
  value,
  onChange,
  label,
  placeholder,
  disabled = false,
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);
  const codes = Array.isArray(value) ? value : [];

  // Confirma lo escrito. Devuelve true si consumió algo (para no propagar la tecla).
  const commitDraft = (raw) => {
    const parts = String(raw ?? "")
      .split(LIST_SEPARATORS_RE)
      .map((p) => p.trim().toUpperCase())
      .filter(Boolean);
    if (parts.length === 0) {
      setDraft("");
      return false;
    }
    // Se descartan los repetidos por su CLAVE canónica ("M5500" y "5500" son la misma
    // regla); los inválidos no tienen clave, así que se comparan por texto.
    const keyOf = (c) => postalRuleKey(c) ?? `raw:${c}`;
    const existing = new Set(codes.map(keyOf));
    const added = [];
    parts.forEach((p) => {
      const k = keyOf(p);
      if (existing.has(k)) return;
      existing.add(k);
      added.push(p);
    });
    setDraft("");
    if (added.length > 0) onChange([...codes, ...added]);
    return true;
  };

  const removeAt = (i) => onChange(codes.filter((_, idx) => idx !== i));

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === ";") {
      e.preventDefault();
      commitDraft(draft);
      return;
    }
    // Backspace con el input vacío borra el último chip (patrón estándar de chips).
    if (e.key === "Backspace" && draft === "" && codes.length > 0) {
      e.preventDefault();
      removeAt(codes.length - 1);
    }
  };

  const invalidCount = codes.filter((c) => !isValidPostalCode(c)).length;

  return (
    <div className="flex flex-col gap-1 w-full">
      {label && <span className="text-xs text-fg-muted">{label}</span>}
      <div
        className={`flex flex-wrap items-center gap-1.5 min-h-9 rounded-md border px-2 py-1 bg-bg ${
          invalidCount > 0 ? "border-status-error" : "border-border"
        } ${disabled ? "opacity-60" : "cursor-text"}`}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {codes.map((code, i) => {
          const invalid = !isValidPostalCode(code);
          return (
            <span
              key={`${code}-${i}`}
              className={`inline-flex items-center gap-1 h-7 pl-2 pr-1 rounded-full text-xs font-medium border ${
                invalid
                  ? "border-status-error bg-status-error/10 text-status-error"
                  : "border-brand-500 bg-brand-50 text-brand-700"
              }`}
              title={
                invalid
                  ? t("Formato inválido: usa un CPA (M5500ABC) o un CP de 4 dígitos (5500). No se guardará.")
                  : undefined
              }
            >
              {invalid && <AlertCircle className="w-3 h-3" aria-hidden="true" />}
              {code}
              <button
                type="button"
                className="h-5 w-5 inline-flex items-center justify-center rounded-full hover:bg-black/10"
                onClick={(e) => {
                  e.stopPropagation();
                  removeAt(i);
                }}
                disabled={disabled}
                aria-label={t("Quitar código postal {{code}}", { code })}
              >
                <X className="w-3 h-3" aria-hidden="true" />
              </button>
            </span>
          );
        })}
        <input
          ref={inputRef}
          type="text"
          className="flex-1 min-w-[120px] h-7 bg-transparent text-sm outline-none"
          value={draft}
          disabled={disabled}
          aria-label={label || t("Códigos postales")}
          aria-invalid={invalidCount > 0 || undefined}
          placeholder={placeholder ?? t("Ej: 5500, M5500ABC")}
          onChange={(e) => setDraft(e.target.value.toUpperCase())}
          onKeyDown={handleKeyDown}
          // Commit al salir del campo: sin esto, lo último escrito se perdería en
          // silencio al hacer clic en "Guardar" (es una tarifa que el operador cree
          // haber configurado).
          onBlur={() => commitDraft(draft)}
        />
      </div>
      {invalidCount > 0 && (
        <span className="text-[11px] text-status-error" role="alert">
          {t("{{count}} código postal con formato inválido: no se guardará.", {
            count: invalidCount,
          })}
        </span>
      )}
    </div>
  );
}
